import { useState } from "react"
import styled from "styled-components"

import { HomeIcon } from "@minuthq/meatball-ui-icons"
import { Banner, Text } from "@minuthq/meatball-ui-react"
import { Badge } from "@minuthq/meatball-ui-react"

import { MonitoringPopup } from "src/components/Homes/HomeDetails/MonitoringPopup"
import { HREF_MINUT_HELP_CROWD_DETECT } from "src/constants/hrefs"
import { THome, Violations } from "src/data/homes/types/homeTypes"
import { useFetchOccupancyThresholdMapping } from "src/data/occupancy/queries/occupancyQueries"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { Divider } from "src/ui/Divider/Divider"
import { QuantitySelector } from "src/ui/Input/QuantitySelector"
import { ExternalLink } from "src/ui/Link/ExternalLink"
import { MCard } from "src/ui/MCard/MCard"
import { spacing } from "src/ui/spacing"

import { PresenceAndCrowdDetectSettings } from "./PresenceAndCrowdDetectSettings"
import { PresenceAndOccupancyToggleIcon } from "./PresenceAndOccupancyToggleIcon"

export function PresenceAndCrowdDetectPopover({
  open,
  activeInitial,
  capacityInitial,
  anchorEl,
  violations,
  update,
  handleClose,
  homeData,
  state = "disabled",
}: {
  open: boolean
  activeInitial: boolean
  capacityInitial: number | undefined
  threshold: number | undefined
  handleClose: () => void
  homeData: THome
  anchorEl: HTMLElement | null
  violations?: Violations[]
  state: "enabled" | "disabled" | undefined
  update: ({
    active,
    capacity,
  }: {
    active?: boolean
    capacity?: number
  }) => void
}) {
  const { t } = useTranslate()
  const fetchOccupancyThresholdMapping = useFetchOccupancyThresholdMapping()
  const [capacity, setCapacity] = useState(capacityInitial || 0)
  const showAlert = !activeInitial && !capacityInitial && capacity < 1
  const calculatedThreshold =
    fetchOccupancyThresholdMapping.data?.[capacity]?.threshold

  // Determine if either feature is currently enabled
  const isMotionAutomationEnabled = state === "enabled"
  const isOccupancyEnabled = activeInitial === true
  const enabled = isMotionAutomationEnabled || isOccupancyEnabled

  /**
   * Calls parent update function to disable both motion automation and occupancy.
   */
  function handleTurnOffClick() {
    onClose({ active: false })
  }

  /**
   * Calls parent update function to enable both motion automation and occupancy.
   */
  function handleTurnOnClick() {
    onClose({ active: true })
  }

  function onClose({ active }: { active?: boolean }) {
    if (active !== undefined || capacity !== capacityInitial) {
      // The idea here is to call update iff active is set to true or false, or the
      // capacity has been changed; that way we won't send a request if the user
      // just opens and then closes the popover
      update({ active, capacity })
    } else {
      handleClose()
    }
  }

  function updateCapacity(value: number) {
    setCapacity(value)
  }

  const buttons = [
    {
      icon: (
        <PresenceAndOccupancyToggleIcon
          toggleState="off"
          occupancyActive={isOccupancyEnabled}
          motionAutomationState={
            isMotionAutomationEnabled ? "enabled" : "disabled"
          }
        />
      ),
      onClick: () => {
        handleTurnOffClick()
      },
      label: t(langKeys.off),
      id: "off",
      disablePointerEvents: !enabled,
    },
    {
      icon: (
        <PresenceAndOccupancyToggleIcon
          toggleState="on"
          occupancyActive={isOccupancyEnabled}
          motionAutomationState={
            isMotionAutomationEnabled ? "enabled" : "disabled"
          }
        />
      ),
      onClick: () => {
        handleTurnOnClick()
      },
      label: t(langKeys.on),
      disableOnWarning: true,
      id: "on",
      disablePointerEvents: enabled,
    },
  ]

  return (
    <MonitoringPopup
      open={open}
      error={false}
      wide
      anchorEl={anchorEl}
      onClose={() => {
        onClose({ active: undefined })
      }}
      title={t(langKeys.motion_and_occupancy_detection)}
      description={t(langKeys.motion_and_occupancy_detection_description)}
      warning={t(langKeys.update_your_plan)}
      violations={violations}
      buttons={buttons}
      subtitle={
        <>
          <StyledHomeIcon width="24px" height="24px" />
          {t(langKeys.home_indoor_label)}
        </>
      }
    >
      {showAlert && (
        <Banner type="info" style={{ marginTop: spacing.M }} fullWidth>
          {t(langKeys.crowd_detect_all_fields_not_set)}
        </Banner>
      )}

      <SettingsBox>
        <Divider />
        <PresenceAndCrowdDetectSettings homeData={homeData} />

        {(homeData.occupancy?.active || showAlert) && (
          <StyledCard padding={spacing.M} boxShadow={false} border>
            <Box>
              <div>
                <Text variant="subtitleS" textAlign="left">
                  {t(langKeys.crowd_detect_capacity_title)}
                </Text>
                <Text variant="bodyS" color="tertiary" textAlign="left">
                  {t(langKeys.crowd_detect_capacity_short_description)}
                </Text>
              </div>
              <QuantitySelector
                value={capacity || "-"}
                onRemove={() => updateCapacity(capacity - 1)}
                onAdd={() => updateCapacity(capacity + 1)}
                disableRemove={capacity <= 1}
                disableAdd={capacity >= 50}
              />
            </Box>
            <Divider />
            <div>
              <Box>
                <Text variant="subtitleS" textAlign="left">
                  {t(langKeys.crowd_detect_chart_threshold_label)}
                </Text>
                <ThresholdBadge>{calculatedThreshold}</ThresholdBadge>
              </Box>
              <Text variant="bodyS" color="tertiary" textAlign="left">
                {t(langKeys.crowd_detect_device_threshold_short_description)}
              </Text>
            </div>
            <Text variant="bodyS" color="tertiary" textAlign="left">
              <ExternalLink
                href={HREF_MINUT_HELP_CROWD_DETECT}
                color="primary"
                showExternalIcon={false}
              >
                {t(langKeys.crowd_detect_device_threshold_read_more)}
              </ExternalLink>
            </Text>
          </StyledCard>
        )}
      </SettingsBox>
    </MonitoringPopup>
  )
}

const SettingsBox = styled.div`
  display: grid;
  margin-top: ${spacing.XL2};
  grid-gap: ${spacing.L};
`

const StyledHomeIcon = styled(HomeIcon)`
  margin-right: ${spacing.XS};
`
const Box = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${spacing.L};
  align-items: center;
`

const ThresholdBadge = styled(Badge)`
  aspect-ratio: 1;
  border-radius: 999px;
`

const StyledCard = styled(MCard)`
  display: grid;
  gap: ${spacing.L};
`
